import {
  requestCallCenter,
  unwrapEnvelopeValue,
} from "./CallCenterApi";
import { listCcAdminRoles } from "./CallCenterRoleAdmin";

/**
 * Call-center workspace user administration (`/api/admin/users`) and role metadata (`/api/security/roles`).
 * Requires JWT with exactly one workspace; responses use the standard envelope.
 */

const CC_USER_LIST_MAX_SIZE = 200;

/**
 * @param {object} [params]
 * @param {number} [params.page]
 * @param {number} [params.size]
 * @param {'NAME'|'EMAIL'|'CREATED_AT'} [params.sortBy]
 * @param {'ASC'|'DESC'} [params.sortDir]
 * @param {number|string} [params.supervisorUserId]
 * @returns {Promise<any>}
 */

export const listCcWorkspaceUsers = async (params = {}) => {
  const { page = 0, size = 20, sortBy = "NAME", sortDir = "ASC", supervisorUserId } = params;
  const safeSize = Math.min(
    CC_USER_LIST_MAX_SIZE,
    Math.max(1, Number.isFinite(Number(size)) ? Number(size) : 20)
  );
  const safePage = Math.max(0, Number.isFinite(Number(page)) ? Number(page) : 0);
  const query = { page: safePage, size: safeSize, sortBy, sortDir };
  const parsedSupervisorId = Number(supervisorUserId);
  if (Number.isFinite(parsedSupervisorId) && parsedSupervisorId > 0) {
    query.supervisorUserId = parsedSupervisorId;
  }
  const envelope = await requestCallCenter({
    method: "GET",
    url: "admin/users",
    params: query,
  });
  return unwrapEnvelopeValue(envelope);
};

/**
 * Active supervisors for agent assignment and user-list filtering.
 */
export const listCcSupervisorOptions = async () => {
  const envelope = await requestCallCenter({
    method: "GET",
    url: "admin/supervisors",
  });
  return unwrapEnvelopeValue(envelope);
};

/**
 * @param {string|number} userId
 */
export const getCcWorkspaceUser = async (userId) => {
  const envelope = await requestCallCenter({
    method: "GET",
    url: `admin/users/${userId}`,
  });
  return unwrapEnvelopeValue(envelope);
};

/**
 * @param {Record<string, unknown>} body CcUserCreateRequest
 */
export const createCcWorkspaceUser = async (body) => {
  const envelope = await requestCallCenter({
    method: "POST",
    url: "admin/users",
    data: body,
  });
  return unwrapEnvelopeValue(envelope);
};

/**
 * @param {string|number} userId
 * @param {Record<string, unknown>} body CcUserUpdateRequest (partial)
 */
export const patchCcWorkspaceUser = async (userId, body) => {
  const envelope = await requestCallCenter({
    method: "PATCH",
    url: `admin/users/${userId}`,
    data: body,
  });
  return unwrapEnvelopeValue(envelope);
};

/**
 * @param {string|number} userId
 * @param {string} newPassword
 */
export const resetCcWorkspaceUserPassword = async (userId, newPassword) => {
  const envelope = await requestCallCenter({
    method: "POST",
    url: `admin/users/${userId}/password`,
    data: { newPassword },
  });
  return unwrapEnvelopeValue(envelope);
};

/**
 * Soft-deactivate user (active = false).
 * @param {string|number} userId
 */
export const deactivateCcWorkspaceUser = async (userId) => {
  const envelope = await requestCallCenter({
    method: "DELETE",
    url: `admin/users/${userId}`,
  });
  return unwrapEnvelopeValue(envelope);
};

/**
 * @param {boolean} active
 */
export const setCcWorkspaceUserActive = async (userId, active) => {
  if (active) {
    return patchCcWorkspaceUser(userId, { active: true });
  }
  return deactivateCcWorkspaceUser(userId);
};

/**
 * Role matrix for admin UI (dropdown + assignable filter).
 */
export const fetchCcSecurityRoles = async () => {
  const envelope = await requestCallCenter({
    method: "GET",
    url: "security/roles",
  });
  return unwrapEnvelopeValue(envelope);
};

/**
 * Assignable roles for user create/edit with numeric roleId for API payloads.
 * Merges security/roles (assignable filter) with admin/roles ids when available.
 * @returns {Promise<Array<{ roleId: number, roleCode: string, displayName: string, description?: string }>>}
 */
export const fetchRolesForUserAssignment = async () => {
  const securityRoles = await fetchCcSecurityRoles();
  const assignable = (Array.isArray(securityRoles) ? securityRoles : []).filter(
    (r) => r?.assignableByCurrentUser !== false
  );

  const idByCode = {};
  try {
    const adminPage = await listCcAdminRoles({
      page: 0,
      size: CC_USER_LIST_MAX_SIZE,
      includeInactive: false,
    });
    (adminPage?.items || []).forEach((r) => {
      if (r?.code != null && r?.id != null) {
        idByCode[String(r.code)] = r.id;
      }
    });
  } catch {
    // Non–super-admin callers may not access admin/roles; rely on ids from security/roles.
  }

  return assignable
    .map((r) => {
      const roleCode = r.role ?? r.code ?? "";
      const roleId = r.id ?? r.roleId ?? idByCode[String(roleCode)] ?? null;
      return {
        roleId,
        roleCode,
        displayName: r.displayName || roleCode,
        description: r.description,
      };
    })
    .filter((r) => r.roleId != null && r.roleCode);
};
